"use client";

import { ShieldCheck, Lock, Landmark, Cpu, Users, Building2 } from "lucide-react";
import { RELATIONSHIP_MANAGERS, branchById } from "@/lib/demos/stockBrokingData";

const PILLARS = [
  { icon: ShieldCheck, title: "SEBI Registered & Regulated", desc: "Registered stock broker & depository participant across NSE, BSE, MCX and NSDL with full regulatory compliance." },
  { icon: Lock, title: "Bank-Grade Security", desc: "256-bit encryption, 2FA login, and funds settled only to your own verified bank account. Your money stays yours." },
  { icon: Landmark, title: "Investor Protection", desc: "Securities held in your own NSDL demat account. Covered under the exchange Investor Protection Fund." },
  { icon: Cpu, title: "Fast, Reliable Platform", desc: "Low-latency order routing, advanced charts, options chains, GTT orders and real-time margin — web & mobile." },
];

export function StockBrokingTrustSection() {
  return (
    <section className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 text-xs font-semibold uppercase tracking-wider mb-3">
            Trust &amp; Safety
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">A broker built on regulation &amp; trust</h2>
          <p className="text-slate-500 text-sm sm:text-base mt-3">Why 2.4 lakh+ investors open and grow their portfolios with Johnson Securities.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-500/30 transition-all group">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-bold text-slate-900 text-base mb-2">{p.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>

        {/* RM roster */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5 mb-6">
            <div>
              <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> Your Relationship Manager Team</h3>
              <p className="text-xs text-slate-500 mt-1">Dedicated, ARN-certified RMs across our branch network — advisory, activation &amp; support.</p>
            </div>
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 inline-flex items-center gap-1.5 self-start sm:self-auto">
              <Building2 className="w-3.5 h-3.5" /> 5 branches
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RELATIONSHIP_MANAGERS.map((rm) => (
              <div key={rm.id} className="p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">{rm.id}</span>
                  <span className="text-[11px] font-bold text-amber-600">★ {rm.rating}</span>
                </div>
                <div className="font-bold text-slate-900 text-sm mt-2">{rm.name}</div>
                <div className="text-[11px] text-slate-500">{rm.role} · {branchById(rm.branchId)?.city}</div>
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-200 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-500 block">Accounts (MTD)</span>
                    <span className="font-semibold text-slate-700">{rm.accountsMtd}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block">Conversion</span>
                    <span className="font-semibold text-emerald-600">{rm.conversion}</span>
                  </div>
                </div>
                {rm.arn && <div className="mt-2 text-[9px] text-slate-500 font-mono">AMFI {rm.arn}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
